import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import {
    HelpCircle, Search, ShoppingCart, Store, Scale, ShieldCheck, CreditCard, Truck,
    UserRound, Trophy, MessageCircle, Mail, BookOpen, Check, ArrowRight, Gavel,
    EyeOff, Clock, Bot, Heart, Target, Users, Sparkles, Star,
} from "lucide-react";

const cx = (...a) => a.filter(Boolean).join(" ");

// ---------------- FAQ content ----------------
const BLIND_BIDDING_ANSWER = "Blind bidding means all bids are hidden until the auction ends. You place one sealed bid — your maximum price — and the highest bidder wins. No one sees what you bid, not even the seller.";

const CATEGORIES = [
    {
        id: "buying", label: "Buying", icon: ShoppingCart, tone: "bg-electric/15 text-electric", tag: "Buyers",
        questions: [
            { q: "What is blind bidding?", a: BLIND_BIDDING_ANSWER, featured: true, beginner: true },
            { q: "Can sellers see my bid?", a: "No. Your bid is fully sealed and encrypted. Sellers — and every other buyer — only ever see the final winning amount once the auction closes." },
            { q: "Can I change my bid?", a: "Bids are final once submitted to keep the playing field fair. You get one honest bid per item, so enter the true maximum you're willing to pay." },
            { q: "What happens if I win?", a: "You'll be notified instantly and prompted to complete secure payment. Once paid, the seller ships your item and you can track it from your buyer dashboard." },
            { q: "What happens if I lose?", a: "Nothing is charged. Your sealed bid simply wasn't the highest. You're free to bid on any other open auction right away." },
            { q: "How many times can I bid on one item?", a: "Once. One sealed bid per buyer, per item — that's what keeps OmniAuc fair for casual bidders and pros alike." },
            { q: "Can I cancel my bid?", a: "Because bids are sealed and binding, they can't be cancelled. Only bid what you're genuinely willing to pay." },
        ],
    },
    {
        id: "selling", label: "Selling", icon: Store, tone: "bg-brass/15 text-brass", tag: "Sellers",
        questions: [
            { q: "Why sell on OmniAuc?", a: "Blind bidding captures each buyer's true willingness-to-pay instead of 'one increment above the next person' — so honest demand often sets a higher final price." },
            { q: "How much does it cost to sell?", a: "Listing is free. A small platform fee is deducted from your payout only when an item sells — no sale, no fee." },
            { q: "When do I get paid?", a: "Payouts are released after the buyer pays and the order is confirmed delivered, minus any platform and shipping costs." },
            { q: "How do I list an item?", a: "Open your Seller dashboard, click Create, add photos, a title, description, starting price, bidder cap, and duration. Our AI can draft a polished description in one click." },
            { q: "What items can I sell?", a: "Most legal, authentic goods across electronics, watches, fashion, collectibles, art, and home. Prohibited and counterfeit items are not allowed." },
            { q: "Can I set a maximum number of bidders?", a: "Yes. Set a bidder cap and the auction closes automatically the moment it's reached — or when the timer ends, whichever comes first." },
        ],
    },
    {
        id: "fairness", label: "Fairness & Blind Bidding", icon: Scale, tone: "bg-purple-500/15 text-purple-400", tag: "Fairness",
        questions: [
            { q: "Why can't I see other bids?", a: "Hidden bids remove anchoring and pressure. Without a visible 'high bid', nobody is nudged up or scared off — your number reflects real value, not reaction." },
            { q: "How is the winner determined?", a: "The highest sealed bid wins. If two bids tie, the earliest submitted bid takes it. The result is computed automatically the instant the auction closes." },
            { q: "Is blind bidding really fair?", a: "Yes — everyone gets exactly one shot and no one can see or beat your number at the last second. Casual bidders compete on equal footing with pros." },
            { q: "Are bots allowed on OmniAuc?", a: "No. Sniping bots are pointless here because bids are sealed and timing is irrelevant. We also actively monitor for manipulation." },
            { q: "What stops shill bidding?", a: "Sellers can't see bids and can't rate buyers, removing the incentive and ability to inflate prices. Suspicious patterns are flagged automatically." },
        ],
    },
    {
        id: "trust", label: "Trust & Safety", icon: ShieldCheck, tone: "bg-success/15 text-success", tag: "Trust",
        questions: [
            { q: "Are sellers verified?", a: "Yes. Sellers complete full identity verification (KYC) before listing, and buyers verify email + phone. Look for the green Verified Seller badge." },
            { q: "What if my item arrives damaged?", a: "Open a return or dispute from your buyer dashboard. Our Trust & Safety team reviews evidence and protects buyers against not-as-described items." },
            { q: "How does dispute resolution work?", a: "Either party can open a dispute on a paid order. Both sides submit details, our team reviews, and we resolve fairly — including refunds where warranted." },
            { q: "Is my personal information safe?", a: "We never expose your email, phone, address, or payment details publicly. Sensitive data is encrypted and protected." },
        ],
    },
    {
        id: "payments", label: "Payments", icon: CreditCard, tone: "bg-rose-500/15 text-rose-400", tag: "Payments",
        questions: [
            { q: "How do payments work?", a: "Winning buyers pay securely through our Stripe-powered checkout. Funds are protected until the order is confirmed." },
            { q: "How do fees work?", a: "Buyers pay only their winning bid plus shipping. Sellers pay a small platform fee deducted from their payout when an item sells." },
            { q: "Do you offer refunds?", a: "Yes — for eligible returns and disputes resolved in the buyer's favor, refunds are issued to the original payment method." },
            { q: "Which payment methods are accepted?", a: "All major credit and debit cards via our secure Stripe checkout. Card details never touch our servers." },
        ],
    },
    {
        id: "shipping", label: "Shipping", icon: Truck, tone: "bg-sky-500/15 text-sky-400", tag: "Shipping",
        questions: [
            { q: "How is shipping handled?", a: "After payment, the seller generates a tracked, insured label. You can follow every step on a live tracking timeline in your buyer dashboard." },
            { q: "How long does delivery take?", a: "It depends on the seller's handling time (shown on their profile and listing) plus the carrier's transit time — typically a few business days." },
            { q: "Can I return an item?", a: "Yes. Request a return from your orders once delivered. If approved, you'll receive return shipping instructions and a refund upon receipt." },
            { q: "Who pays for shipping?", a: "Buyers see shipping at checkout. Sellers fund the outbound label (deducted from payout). Return shipping terms follow the seller's return policy." },
        ],
    },
    {
        id: "accounts", label: "Accounts", icon: UserRound, tone: "bg-pink-500/15 text-pink-400", tag: "Accounts",
        questions: [
            { q: "How do I create an account?", a: "Click Create account, choose whether you want to buy, sell, or both, then verify your email and phone to start bidding." },
            { q: "How do I become a verified seller?", a: "Complete identity verification (KYC) in your Verification settings. Once approved, you'll get the Verified Seller badge and can list items." },
            { q: "Can I be both a buyer and a seller?", a: "Absolutely. One account can do both — switch between your buyer and seller dashboards anytime." },
            { q: "How do I edit my public seller profile?", a: "In your Seller dashboard, open the Profile tab to set your store name, bio, photo, banner, location, handling time, and return policy." },
        ],
    },
    {
        id: "winning", label: "Winning Auctions", icon: Trophy, tone: "bg-amber-500/15 text-amber-400", tag: "Winning",
        questions: [
            { q: "How will I know if I won?", a: "You'll get an instant notification, and the win appears in your buyer dashboard with a prompt to complete secure payment." },
            { q: "How long do I have to pay?", a: "Please complete payment promptly after winning so the seller can ship without delay. You'll see clear reminders in your dashboard." },
            { q: "Can I leave a review after winning?", a: "Yes — once your order is delivered, you can rate the seller (item as described, shipping speed, communication) and leave a written review." },
            { q: "What if I win and change my mind?", a: "Winning bids are binding. If there's a genuine problem with the item, use returns or disputes — but a win is a commitment to purchase." },
        ],
    },
];

const COMPARISON_ROWS = [
    { icon: Users, old: "Bidding wars drive prices up", neo: "One honest bid reveals true value" },
    { icon: Clock, old: "Last-second sniping", neo: "No sniping. Impossible." },
    { icon: Bot, old: "Bots & shill bidding manipulate auctions", neo: "Equal opportunity for everyone" },
    { icon: EyeOff, old: "Visible bids create pressure", neo: "Bids stay hidden. Zero pressure." },
    { icon: Heart, old: "Stressful & exhausting", neo: "Simple, transparent & fair" },
    { icon: Target, old: "Strategic timing matters most", neo: "Honest value matters most" },
];

const CONFIDENCE_POINTS = [
    "Verified sellers you can trust",
    "Secure payments powered by Stripe",
    "Dispute resolution & buyer protection",
    "Fraud monitoring & prevention",
    "Encrypted & secure transactions",
];

// ---------------- Sub-components ----------------
function BlindBiddingTable() {
    const trad = [["Bidder A", "$50"], ["Bidder A", "$51"], ["Bidder A", "$52"], ["Bidder B", "$53"]];
    const neo = [["Bidder A", "$75"], ["Bidder B", "$60"], ["Bidder C", "$80"]];
    return (
        <div className="mt-4 grid gap-3 rounded-xl border border-border bg-background/60 p-4 sm:grid-cols-2">
            <div>
                <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-rose-400"><Gavel className="h-4 w-4" /> Traditional Auctions</p>
                <ul className="space-y-1.5 text-sm">
                    {trad.map(([b, p], i) => (
                        <li key={i} className="flex items-center justify-between text-muted-foreground"><span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-rose-400/70" />{b}</span><span>{p}</span></li>
                    ))}
                </ul>
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm font-bold text-rose-400"><span>Winner</span><span>$53</span></div>
                <p className="mt-1 text-xs text-muted-foreground">Bidding wars & sniping</p>
            </div>
            <div className="sm:border-l sm:border-border sm:pl-4">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-success"><Gavel className="h-4 w-4" /> OmniAuc Blind Bidding</p>
                <ul className="space-y-1.5 text-sm">
                    {neo.map(([b, p], i) => (
                        <li key={i} className="flex items-center justify-between text-muted-foreground"><span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-success/70" />{b}</span><span>{p}</span></li>
                    ))}
                </ul>
                <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-sm font-bold text-success"><span>Winner</span><span>$80</span></div>
                <p className="mt-1 text-xs text-muted-foreground">One fair shot. Honest price.</p>
            </div>
        </div>
    );
}

function QuestionAccordion({ items, tagTone = "bg-electric/15 text-electric", testId }) {
    return (
        <Accordion type="single" collapsible className="w-full" data-testid={testId}>
            {items.map((it, i) => (
                <AccordionItem key={`${it.q}-${i}`} value={`${it.q}-${i}`} className="border-border">
                    <AccordionTrigger className="gap-3 text-left text-sm font-medium hover:no-underline">
                        <span className="flex flex-1 items-center gap-2">
                            {it.q}
                            {it.beginner && <Badge variant="secondary" className="hidden gap-1 text-[10px] sm:inline-flex"><Sparkles className="h-3 w-3 text-brass" />Beginner Friendly</Badge>}
                        </span>
                        {it.tag && <Badge className={cx("mr-2 shrink-0 border-0 text-[10px] font-medium", tagTone)}>{it.tag}</Badge>}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                        {it.a}
                        {it.featured && <BlindBiddingTable />}
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    );
}

const SUPPORT = [
    { icon: MessageCircle, title: "Live Chat", sub: "Chat now", tone: "bg-success/15 text-success", action: "chat" },
    { icon: Mail, title: "Contact Support", sub: "Send an email", tone: "bg-electric/15 text-electric", href: "mailto:support@omniauc.com" },
    { icon: BookOpen, title: "Help Center", sub: "Visit center", tone: "bg-brass/15 text-brass", to: "/trust-safety" },
];

// ---------------- Page ----------------
export default function FAQ() {
    const [activeCat, setActiveCat] = useState("buying");
    const [query, setQuery] = useState("");

    const searching = query.trim().length > 0;
    const searchResults = useMemo(() => {
        if (!searching) return [];
        const q = query.toLowerCase();
        const out = [];
        for (const c of CATEGORIES) {
            for (const item of c.questions) {
                if (item.q.toLowerCase().includes(q) || item.a.toLowerCase().includes(q)) {
                    out.push({ ...item, tag: c.label });
                }
            }
        }
        return out;
    }, [query, searching]);

    const active = CATEGORIES.find((c) => c.id === activeCat) || CATEGORIES[0];
    const sellingCat = CATEGORIES.find((c) => c.id === "selling");
    const popular = [
        { q: "Can I cancel my bid?", cat: "buying" },
        { q: "How do fees work?", cat: "payments" },
        { q: "Do you offer refunds?", cat: "payments" },
        { q: "How is shipping handled?", cat: "shipping" },
        { q: "What if my item arrives damaged?", cat: "trust" },
    ];

    const goToCategory = (id) => {
        setActiveCat(id);
        setQuery("");
        document.getElementById("faq-browse")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div data-testid="faq-page">
            {/* Hero */}
            <section className="noise-overlay relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_85%_0%,rgba(240,180,41,0.16)_0%,rgba(240,180,41,0)_55%),radial-gradient(50%_50%_at_10%_10%,rgba(46,139,255,0.18)_0%,rgba(46,139,255,0)_55%)]" />
                <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-20">
                    <div>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
                            <HelpCircle className="h-3.5 w-3.5 text-brass" /> FAQ Center
                        </span>
                        <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                            Questions?<br />We&apos;ve Got <span className="text-brass">Answers.</span>
                        </h1>
                        <p className="mt-4 max-w-lg text-base text-muted-foreground">
                            Learn how blind bidding works, why OmniAuc is different, and how we&apos;re creating a fairer marketplace for buyers and sellers.
                        </p>
                        <div className="relative mt-7 max-w-xl">
                            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search frequently asked questions..."
                                className="h-14 rounded-xl pl-12 text-base"
                                data-testid="faq-search"
                            />
                        </div>
                    </div>
                    <div className="relative hidden h-64 items-center justify-center lg:flex">
                        <div className="absolute inset-0 -z-10 rounded-full bg-brass/10 blur-3xl" />
                        <Gavel className="h-40 w-40 -rotate-12 text-brass drop-shadow-[0_10px_30px_rgba(240,180,41,0.35)]" strokeWidth={1.2} />
                        <HelpCircle className="absolute left-6 top-6 h-10 w-10 text-electric/70" />
                        <HelpCircle className="absolute right-10 top-2 h-14 w-14 text-brass/80" />
                        <HelpCircle className="absolute bottom-6 right-2 h-8 w-8 text-electric/60" />
                        <HelpCircle className="absolute bottom-2 left-16 h-7 w-7 text-brass/50" />
                    </div>
                </div>
            </section>

            {/* Category tiles */}
            <section id="faq-browse" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-8">
                    {CATEGORIES.map((c) => {
                        const Icon = c.icon;
                        const isActive = !searching && c.id === activeCat;
                        return (
                            <button
                                key={c.id}
                                onClick={() => goToCategory(c.id)}
                                data-testid={`faq-cat-${c.id}`}
                                className={cx(
                                    "flex flex-col items-center gap-2 rounded-2xl border p-4 text-center transition-[transform,border-color,background-color] duration-200 hover:-translate-y-[2px]",
                                    isActive ? "border-electric bg-electric/[0.06]" : "border-border bg-card hover:border-electric/40",
                                )}
                            >
                                <span className={cx("flex h-12 w-12 items-center justify-center rounded-xl", c.tone)}><Icon className="h-6 w-6" /></span>
                                <span className="font-display text-sm font-bold leading-tight">{c.label}</span>
                                <span className="text-xs text-muted-foreground">{c.questions.length} questions</span>
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Search results OR Top questions + Why different */}
            {searching ? (
                <section className="mx-auto max-w-3xl px-4 pb-16 sm:px-6">
                    <h2 className="mb-4 font-display text-xl font-bold">
                        {searchResults.length} result{searchResults.length === 1 ? "" : "s"} for &ldquo;{query}&rdquo;
                    </h2>
                    {searchResults.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border p-10 text-center">
                            <Search className="mx-auto h-7 w-7 text-muted-foreground/50" />
                            <p className="mt-3 text-sm text-muted-foreground">No matching questions. Try different keywords or browse a category above.</p>
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-border bg-card p-2 sm:p-4">
                            <QuestionAccordion items={searchResults} tagTone="bg-secondary text-muted-foreground" testId="faq-search-results" />
                        </div>
                    )}
                </section>
            ) : (
                <section className="mx-auto grid max-w-7xl gap-6 px-4 pb-16 sm:px-6 lg:grid-cols-[1.3fr_0.7fr]">
                    {/* Top questions */}
                    <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
                        <div className="flex items-center gap-2">
                            <span className={cx("flex h-9 w-9 items-center justify-center rounded-lg", active.tone)}><active.icon className="h-5 w-5" /></span>
                            <h2 className="font-display text-xl font-bold">{active.label} questions</h2>
                        </div>
                        <div className="mt-4">
                            <QuestionAccordion items={active.questions} tagTone={active.tone} testId="faq-top-questions" />
                        </div>
                        <Button asChild variant="outline" className="mt-5 w-full rounded-[10px] border-brass/40 text-brass hover:bg-brass/10" data-testid="faq-view-all">
                            <Link to="/trust-safety">View more help in our Help Center <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                        </Button>
                    </div>

                    {/* Why OmniAuc is different + support */}
                    <div className="space-y-6">
                        <div className="rounded-2xl border border-border bg-card p-6">
                            <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Scale className="h-5 w-5 text-brass" /> Why OmniAuc is different</h2>
                            <div className="mt-4 grid grid-cols-2 gap-1.5 text-center text-xs font-semibold">
                                <span className="rounded-lg border border-border bg-background/60 px-2 py-1.5 text-muted-foreground">Traditional</span>
                                <span className="rounded-lg bg-brass px-2 py-1.5 text-brass-foreground">OmniAuc</span>
                            </div>
                            <ul className="mt-3 space-y-2">
                                {COMPARISON_ROWS.map((r) => {
                                    const Icon = r.icon;
                                    return (
                                        <li key={r.old} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg border border-border bg-background/40 p-2.5">
                                            <span className="text-xs text-muted-foreground">{r.old}</span>
                                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Icon className="h-3.5 w-3.5" /></span>
                                            <span className="text-right text-xs font-semibold text-brass">{r.neo}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        <div className="rounded-2xl border border-border bg-card p-6">
                            <h3 className="font-display text-lg font-bold">Still need help?</h3>
                            <p className="mt-1 text-sm text-muted-foreground">Our support team is here for you.</p>
                            <div className="mt-4 space-y-2.5">
                                {SUPPORT.map((s) => {
                                    const Icon = s.icon;
                                    const inner = (
                                        <span className="flex items-center gap-3">
                                            <span className={cx("flex h-9 w-9 items-center justify-center rounded-lg", s.tone)}><Icon className="h-4 w-4" /></span>
                                            <span><span className="block text-sm font-semibold">{s.title}</span><span className="block text-xs text-muted-foreground">{s.sub}</span></span>
                                        </span>
                                    );
                                    const cls = "flex items-center justify-between rounded-xl border border-border bg-background/40 p-3 transition-colors hover:border-electric/40";
                                    if (s.to) return <Link key={s.title} to={s.to} className={cls} data-testid="faq-support-help">{inner}<ArrowRight className="h-4 w-4 text-muted-foreground" /></Link>;
                                    if (s.href) return <a key={s.title} href={s.href} className={cls} data-testid="faq-support-email">{inner}<ArrowRight className="h-4 w-4 text-muted-foreground" /></a>;
                                    return <button key={s.title} onClick={() => toast.success("Live chat is coming soon — email support@omniauc.com for now.")} className={cx(cls, "w-full text-left")} data-testid="faq-support-chat">{inner}<ArrowRight className="h-4 w-4 text-muted-foreground" /></button>;
                                })}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Three info cards */}
            {!searching && (
                <section className="mx-auto grid max-w-7xl gap-6 px-4 pb-16 sm:px-6 lg:grid-cols-3">
                    <div className="rounded-2xl border border-border bg-card p-6">
                        <h3 className="flex items-center gap-2 font-display text-lg font-bold"><Store className="h-5 w-5 text-brass" /> Selling made simple</h3>
                        <ul className="mt-4 space-y-1.5">
                            {sellingCat.questions.slice(0, 5).map((it) => (
                                <li key={it.q}>
                                    <button onClick={() => goToCategory("selling")} className="flex w-full items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2 text-left text-sm transition-colors hover:border-brass/40" data-testid="faq-selling-link">
                                        <span>{it.q}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <Button onClick={() => goToCategory("selling")} variant="outline" className="mt-4 w-full rounded-[10px] border-brass/40 text-brass hover:bg-brass/10">View all selling questions</Button>
                    </div>

                    <div className="rounded-2xl border border-border bg-card p-6">
                        <h3 className="flex items-center gap-2 font-display text-lg font-bold"><ShieldCheck className="h-5 w-5 text-success" /> Buy with confidence</h3>
                        <ul className="mt-4 space-y-2.5">
                            {CONFIDENCE_POINTS.map((p) => (
                                <li key={p} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/15 text-success"><Check className="h-3.5 w-3.5" /></span>{p}
                                </li>
                            ))}
                        </ul>
                        <Button asChild variant="outline" className="mt-4 w-full rounded-[10px] border-success/40 text-success hover:bg-success/10">
                            <Link to="/trust-safety">Learn more about safety</Link>
                        </Button>
                    </div>

                    <div className="rounded-2xl border border-border bg-card p-6">
                        <h3 className="flex items-center gap-2 font-display text-lg font-bold"><Star className="h-5 w-5 text-brass" /> Popular questions</h3>
                        <ul className="mt-4 space-y-1.5">
                            {popular.map((p) => (
                                <li key={p.q}>
                                    <button onClick={() => goToCategory(p.cat)} className="flex w-full items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2 text-left text-sm transition-colors hover:border-electric/40" data-testid="faq-popular-link">
                                        <span>{p.q}</span><ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <Button onClick={() => goToCategory("buying")} variant="outline" className="mt-4 w-full rounded-[10px] border-electric/40 text-electric hover:bg-electric/10">View more popular questions</Button>
                    </div>
                </section>
            )}

            {/* CTA band */}
            <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6">
                <div className="noise-overlay relative overflow-hidden rounded-2xl border border-border p-10 text-center sm:p-14" style={{ background: "linear-gradient(135deg, hsl(221 55% 14%), hsl(212 90% 26%))" }}>
                    <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Ready to experience <span className="text-brass">fair auctions?</span></h2>
                    <p className="mx-auto mt-3 max-w-xl text-white/80">No bidding wars. No sniping. No guesswork. Just one honest bid.</p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" className="rounded-[10px] font-semibold" data-testid="faq-cta-browse"><Link to="/auctions">Browse Auctions</Link></Button>
                        <Button asChild size="lg" variant="outline" className="rounded-[10px] border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="faq-cta-sell"><Link to="/dashboard/seller">Start Selling</Link></Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
